// Pure functions for the want page (#222; spec
// docs/superpowers/specs/2026-11-02-wantlist-redesign-design.md): the shelf
// of bundles, the card rows with their sort keys, page slicing and the shelf
// carousel's paging. No side effects.

import { pageCount, clampPage } from './card-collection-logic.js';
import { extractSourceInfo } from './url-parser.js';

// ---------------------------------------------------------------------------
// The shelf: [defaultBundle, ...projects]
// ---------------------------------------------------------------------------

/** The Default Want List's bundle id (never a binder id: it has no binder). */
export const DEFAULT_BUNDLE_ID = 'default';

/** @typedef {import('../types.js').WantEntry} WantEntry */

/**
 * A Project as the shelf reads it. Its Source Link and commanders may be
 * absent (not yet synced, or a deck with no commander); the shelf degrades.
 * @typedef {import('../types.js').Project} ShelfProject
 */

/** @typedef {{ name: string, icon: string }} Site */
/** @typedef {{ key: string, scryfallId: string, front: boolean }} FanCard */

/**
 * @typedef {Object} Bundle
 * @property {string} id - DEFAULT_BUNDLE_ID or the Project's binder_id
 * @property {'default' | 'project'} kind
 * @property {string} name
 * @property {number} count
 * @property {WantEntry[]} entries - the bundle's want entries, as fetched
 * @property {ShelfProject | null} project
 * @property {FanCard[]} fan - the tile's cover (fanOf)
 * @property {Site | null} site - where the bundle comes from, for its badge
 */

/**
 * The owner's writes on their want page (WantPage does them; the Wants tab
 * asks). A visitor's page has none (null), which is what hides every
 * owner-only control.
 * @typedef {Object} OwnerWrites
 * @property {(name: string) => Promise<number | null>} addCard - the new
 *   entry's id, or null when the add failed
 * @property {(entryId: number) => void} removeEntry
 * @property {(entryId: number, spec: import('./card-options-logic.js').Spec) => Promise<void>} setSpecificity
 *   - re-point an entry's Card Specificity (the card options), after any
 *   earlier re-point of it; resolves once written or refused. A re-point
 *   onto a specificity the list already holds merges into that entry
 *   (stores/want-page `survivorOf` then finds it)
 * @property {(link: string) => Promise<{ ok: boolean, error?: string }>} addProject
 * @property {(projectBinderId: string) => void} removeProject
 */

/** Our own mark: the Default Want List's badge. */
export const OWN_SITE = Object.freeze({ name: 'Binder Browser', icon: '/bb_logo.svg' });

/** Source sites by `extractSourceInfo` source, each with its bundled icon. */
const SITES = /** @type {Record<string, Site>} */ ({
  moxfield: { name: 'Moxfield', icon: '/sites/moxfield.svg' },
  archidekt: { name: 'Archidekt', icon: '/sites/archidekt.svg' },
  scryfall: { name: 'Scryfall', icon: '/sites/scryfall.svg' },
  cubecobra: { name: 'CubeCobra', icon: '/sites/cubecobra.svg' },
});

/**
 * The sites a Project can come from, as the New project modal shows them
 * (spec "Wants" > Shelf: source sites shown as icons), each with the
 * `extractSourceInfo` source it stands for.
 * @type {ReadonlyArray<Site & { source: string }>}
 */
export const SOURCE_SITES = Object.freeze(
  ['moxfield', 'archidekt', 'cubecobra', 'scryfall'].map((source) => ({
    source,
    ...SITES[source],
  }))
);

/**
 * The site a link being typed or pasted belongs to, so the modal can light
 * its icon: the parsed source of a whole link or short code, else a site's
 * name anywhere in the text (lights while "moxfield.com/de..." is typed).
 * @param {string} link
 * @returns {string | null} the source, e.g. 'moxfield'
 */
export function siteOfLink(link) {
  const text = (link || '').trim().toLowerCase();
  if (!text) return null;
  const parsed = extractSourceInfo(text)?.source;
  if (parsed && SITES[parsed]) return parsed;
  return SOURCE_SITES.find((s) => text.includes(s.source))?.source ?? null;
}

/**
 * What the New project modal says when a link is refused: the cap
 * (CONTEXT.md "Project cap", a 409 `cap_full`; `cap` is the server's, sent
 * with the projects), else the server's reason for a link it cannot read or
 * fetch.
 * @param {{ status: number, error?: string }} res
 * @param {number} cap
 * @returns {string}
 */
export function projectAddError(res, cap) {
  if (res.error === 'cap_full') {
    return `You already have ${cap} projects. Remove one to make room.`;
  }
  if (res.status === 0) return "Couldn't reach Binder Browser. Try again.";
  return res.error || "That link isn't a deck or cube we can read.";
}

/**
 * The site a Project comes from, read off its Source Link or, until it has
 * one, its short URL (url-parser's one table of sources). Null when unknown.
 * @param {{ short_url?: string, source_url?: string | null }} project
 * @returns {Site | null}
 */
export function siteOf(project) {
  const source = extractSourceInfo(project.source_url || project.short_url || '')?.source;
  return (source && SITES[source]) || null;
}

/**
 * The shelf, in order: the Default Want List (every entry that is not a
 * Project Entry; CONTEXT.md "Default Want List"), then each Project with its
 * own entries. The default is built here, never through a Project API: it
 * has no binder, cap, registration or Source Link. Each bundle carries its
 * fan and badge, computed once here.
 * @param {WantEntry[] | null | undefined} entries
 * @param {ShelfProject[] | null | undefined} projects
 * @returns {Bundle[]}
 */
export function buildShelf(entries, projects) {
  /** @type {Map<string, WantEntry[]>} */
  const byProject = new Map();
  /** @type {WantEntry[]} */
  const own = [];
  for (const e of entries || []) {
    if (e.project_binder_id == null) own.push(e);
    else {
      const rows = byProject.get(e.project_binder_id) ?? [];
      rows.push(e);
      byProject.set(e.project_binder_id, rows);
    }
  }
  return [
    {
      id: DEFAULT_BUNDLE_ID,
      kind: 'default',
      name: 'Want list',
      count: own.length,
      entries: own,
      project: null,
      fan: fanOf(own, null),
      site: OWN_SITE,
    },
    ...(projects || []).map((p) => {
      const rows = byProject.get(p.binder_id) ?? [];
      return /** @type {Bundle} */ ({
        id: p.binder_id,
        kind: 'project',
        name: p.name || (p.short_url || '').replace(/^\//, ''),
        count: p.card_count ?? rows.length,
        entries: rows,
        project: p,
        fan: fanOf(rows, p),
        site: siteOf(p),
      });
    }),
  ];
}

/**
 * A bundle tile's cover: up to three cards fanned left / centre / right, its
 * most valuable wants. A commander deck puts its commander in the centre, on
 * top (`front`), whether or not the commander itself is wanted. Cards with no
 * image are skipped.
 * @param {WantEntry[]} entries
 * @param {{ commanders?: Array<{ name: string, scryfall_id: string | null }> } | null} project
 * @returns {FanCard[]}
 */
export function fanOf(entries, project) {
  const commander = (project?.commanders ?? []).find((c) => c.scryfall_id);
  const top = entries
    .filter((e) => e.sample_scryfall_id && e.sample_scryfall_id !== commander?.scryfall_id)
    .sort((a, b) => (b.sample_price ?? 0) - (a.sample_price ?? 0))
    .map((e) => ({ key: `e${e.id}`, scryfallId: String(e.sample_scryfall_id), front: false }));
  if (!commander?.scryfall_id) return top.slice(0, 3);
  const face = { key: 'commander', scryfallId: commander.scryfall_id, front: true };
  const [left, right] = top;
  return [left, face, right].filter(Boolean);
}

/**
 * A tile title split before its last word, so the count can be glued to that
 * word and never sits on a line of its own (VISUALS.md "Rows of tiles are
 * carousels").
 * @param {string} title
 * @returns {{ head: string, tail: string }}
 */
export function titleParts(title) {
  const cut = title.lastIndexOf(' ') + 1;
  return { head: title.slice(0, cut), tail: title.slice(cut) };
}

// ---------------------------------------------------------------------------
// The picked bundle's rows: one representative printing per row
// ---------------------------------------------------------------------------

/**
 * The one set a row shows, as the server decides it
 * (`want_list_sql._display_set`).
 * @typedef {Object} RowSet
 * @property {string} code
 * @property {string} name
 * @property {string | null} releasedAt - ISO date; the Set column sorts by it
 * @property {boolean} standIn - the Sample Card's set standing in for "any
 *   printing" (CONTEXT.md "Retail Set"): shown with an asterisk
 * @property {string | null} rarity - the symbol's colour: the card's rarity
 *   when this set IS the representative printing's, else null (common)
 */

/**
 * @typedef {Object} WantRow
 * @property {number} id
 * @property {string} name
 * @property {string | null} scryfallId
 * @property {number | null} price
 * @property {boolean} isSample - the price is the "from" floor
 * @property {RowSet | null} set
 * @property {string | null} collectorNumber - only when the want pins a printing
 * @property {string | null} finish
 * @property {'foil' | 'etched' | null} mark - the finish mark (non-foil and any carry none)
 * @property {string | null} style - the pinned printing's Style Descriptor
 */

/**
 * One table row / card tile from a fetched want entry
 * (`want_list_sql.enrich_entries`): a mapping of what the server sent. The
 * set is its `display_set`. The Style Descriptor shows only for a pinned
 * printing that resolved (CONTEXT.md "Style Descriptor").
 * @param {WantEntry} entry
 * @returns {WantRow}
 */
export function wantRow(entry) {
  const shown = entry.display_set ?? null;
  const finish = entry.finish ?? null;
  const pinned = !!(entry.set_code && entry.collector_number && !entry.is_sample);
  return {
    id: entry.id,
    name: entry.card_name,
    scryfallId: entry.sample_scryfall_id ?? null,
    price: entry.sample_price ?? null,
    isSample: !!entry.is_sample,
    set: shown && {
      code: shown.code,
      name: shown.name,
      releasedAt: shown.released_at,
      standIn: shown.stand_in,
      rarity: shown.rarity,
    },
    collectorNumber: entry.collector_number ?? null,
    finish,
    mark: finish === 'foil' || finish === 'etched' ? finish : null,
    style: pinned ? (entry.variant ?? null) : null,
  };
}

/** @typedef {'name' | 'set' | 'price'} SortKey */
/** @typedef {{ key: SortKey, dir: 1 | -1 }} SortState */

/** Each sortable column's first direction: names A-Z, newest set, priciest. */
const FIRST_DIR = /** @type {Record<SortKey, 1 | -1>} */ ({ name: 1, set: -1, price: -1 });

export const DEFAULT_SORT = /** @type {SortState} */ ({ key: 'name', dir: 1 });

/**
 * @param {WantRow} row
 * @param {SortKey} key
 * @returns {string | number | null}
 */
function sortValue(row, key) {
  if (key === 'name') return row.name.toLowerCase();
  if (key === 'price') return row.price;
  return row.set?.releasedAt ?? null;
}

/**
 * The rows in a column's order: Name, Set (by release date; a set-less want
 * by its Sample Card's) or Price. A row with no value sorts last in either
 * direction; ties go by name. Returns a new array.
 * @param {WantRow[]} rows
 * @param {SortState} sort
 * @returns {WantRow[]}
 */
export function sortRows(rows, { key, dir }) {
  const byName = (/** @type {WantRow} */ a, /** @type {WantRow} */ b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  return [...rows].sort((a, b) => {
    const x = sortValue(a, key);
    const y = sortValue(b, key);
    if (x == null || y == null) return Number(x == null) - Number(y == null) || byName(a, b);
    const d = typeof x === 'string' ? x.localeCompare(String(y)) : x - Number(y);
    return dir * d || byName(a, b);
  });
}

/**
 * The rows with the cards added this visit first, newest on top (spec
 * "Wants": the new card lands as the first row), the rest in their sorted
 * order. Returns a new array; with nothing recent, the rows as they are.
 * @template {{ id: number }} R
 * @param {R[]} rows
 * @param {number[]} recentIds - newest first
 * @returns {R[]}
 */
export function recentFirst(rows, recentIds) {
  if (!recentIds.length) return rows;
  const rank = new Map(recentIds.map((id, i) => [id, i]));
  const recent = rows.filter((r) => rank.has(r.id));
  recent.sort((a, b) => Number(rank.get(a.id)) - Number(rank.get(b.id)));
  return [...recent, ...rows.filter((r) => !rank.has(r.id))];
}

/**
 * The entries after entry `id` was re-pointed and the server answered
 * `next`: the answer takes the old entry's place. A re-point onto a Card
 * Specificity the list already holds merges into that entry (the server's
 * dedup), so `next` may be another entry: its own row goes. An entry no
 * longer listed leaves the list as it is.
 * @template {{ id: number }} E
 * @param {E[]} entries
 * @param {number} id
 * @param {E} next
 * @returns {E[]}
 */
export function repointEntries(entries, id, next) {
  if (!entries.some((e) => e.id === id)) return entries;
  return entries
    .filter((e) => e.id === id || e.id !== next.id)
    .map((e) => (e.id === id ? next : e));
}

/**
 * The sort after a header tap: the sorted column flips, another column
 * starts in its first direction.
 * @param {SortState} current
 * @param {SortKey} key
 * @returns {SortState}
 */
export function nextSort(current, key) {
  if (current.key === key) return { key, dir: current.dir === 1 ? -1 : 1 };
  return { key, dir: FIRST_DIR[key] };
}

/**
 * One numbered page of rows, the requested page clamped into range.
 * @template T
 * @param {T[]} rows
 * @param {number} page
 * @param {number} size
 * @returns {{ rows: T[], page: number, last: number }}
 */
export function pageOf(rows, page, size) {
  const last = pageCount(rows.length, size);
  const safe = clampPage(page, last);
  return { rows: rows.slice((safe - 1) * size, safe * size), page: safe, last };
}

// ---------------------------------------------------------------------------
// The shelf carousel (VISUALS.md "Rows of tiles are carousels")
// ---------------------------------------------------------------------------

/**
 * How far a desktop chevron scrolls the shelf: the whole tiles that fit in
 * the view (at least one).
 * @param {{ viewport: number, tile: number, gap: number }} m - widths in px
 * @returns {number}
 */
export function shelfStep({ viewport, tile, gap }) {
  const stride = tile + gap;
  return Math.max(1, Math.floor((viewport + gap) / stride)) * stride;
}

/**
 * Which chevrons show: only where there is more that way (2px of slack for
 * sub-pixel scroll positions). `endPadding` is the shelf's trailing padding
 * (it reaches to the screen's edge), which is not more to see.
 * @param {{ scrollLeft: number, clientWidth: number, scrollWidth: number, endPadding?: number }} m
 * @returns {{ canPrev: boolean, canNext: boolean }}
 */
export function shelfEdges({ scrollLeft, clientWidth, scrollWidth, endPadding = 0 }) {
  return {
    canPrev: scrollLeft > 2,
    canNext: scrollLeft + clientWidth < scrollWidth - endPadding - 2,
  };
}

/**
 * Project slots still free: the "New project" tile shows them.
 * @param {unknown[] | null | undefined} projects
 * @param {number} cap - CONTEXT.md "Project cap", as the server sends it
 */
export function projectSlotsLeft(projects, cap) {
  return Math.max(0, cap - (projects?.length ?? 0));
}
